import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/activity-report/ActivityReportAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useActivityReportUIContext } from "./ActivityReportUIContext";
import { ActivityReportFilter } from "./activity-report-filter/ActivityReportFilter";
import { ActivityReportTable } from "./activity-report-table/ActivityReportTable";

export function ActivityReportCard() {
  // Activity Report Card UI Context
  const activityReportUIContext = useActivityReportUIContext();
  const activityReportUIProps = useMemo(() => {
    return {
      queryParams: activityReportUIContext.queryParams
    };
  }, [activityReportUIContext]);

  // Getting curret state of Activity Report list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.activityReport }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Activity Report Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Faaliyet Raporunu Excele Aktarır */
  const exportActivityReportToExcel = () => {
    dispatch(actions.getAllActivityReport(activityReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [25, 30, 30, 30, 50, 75, 20, 40];
      const columns = ["Sınıf", "Öğretmen", "Öğrenci", "Faaliyet Türü", "Başlık", "Açıklama", "Tarih", "Ek"];
      entities.forEach(entity => rows.push([entity.section, entity.teacher, entity.student, entity.activityType, entity.title, entity.explanation, entity.date, entity.attachmentName]));
      const filename = activityReportUIProps.queryParams.term + " - " + (activityReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Faaliyet Raporu";
      const sheetname = "Faaliyet Raporu"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Faaliyet Raporunu PDF e Aktarır */
  const exportActivityReportToPDF = () => {
    dispatch(actions.getAllActivityReport(activityReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Öğretmen",
          rows: entities.map(entity => entity.teacher)
        },
        {
          header: "Öğrenci",
          rows: entities.map(entity => entity.student)
        },
        {
          header: "Faaliyet Türü",
          rows: entities.map(entity => entity.activityType)
        },
        {
          header: "Başlık",
          rows: entities.map(entity => entity.title)
        },
        {
          header: "Açıklama",
          rows: entities.map(entity => entity.explanation)
        },
        {
          header: "Tarih",
          rows: entities.map(entity => entity.date)
        },
        {
          header: "Ek",
          rows: entities.map(entity => entity.attachmentName)
        }        
      ];
      
      const filename = activityReportUIProps.queryParams.term + " - " + (activityReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Faaliyet Raporu";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Faaliyet Raporunu Yazdırma Penceresini Açar */
  const printActivityReport = () => {
    dispatch(actions.getAllActivityReport(activityReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];
      const header = activityReportUIProps.queryParams.term + " - " + (activityReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Faaliyet Raporu";
      const properties = ["Sınıf", "Öğretmen", "Öğrenci", "Faaliyet Türü", "Başlık", "Açıklama", "Tarih", "Ek"];

      entities.forEach(entity => {
        data.push({          
          "Sınıf": entity.section,
          "Öğretmen": entity.teacher,
          "Öğrenci": entity.student,
          "Faaliyet Türü": entity.activityType,
          "Başlık": entity.title,
          "Açıklama": entity.explanation,
          "Tarih": entity.date,
          "Ek": entity.attachmentName ?? ""
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Faaliyet Raporunu Panoya Kopyalar */
  const copyActivityReport = () => {
    dispatch(actions.getAllActivityReport(activityReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Sınıf", "Öğretmen", "Öğrenci", "Faaliyet Türü", "Başlık", "Açıklama", "Tarih", "Ek"];
      const header = activityReportUIProps.queryParams.term + " - " + (activityReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Faaliyet Raporu";
      entities.forEach(entity => rows.push([entity.section, entity.teacher, entity.student, entity.activityType, entity.title, entity.explanation, entity.date, entity.attachmentName ?? ""]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Faaliyet Raporu">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportActivityReportToExcel}
              exportToPDF={exportActivityReportToPDF}
              print={printActivityReport}
              copy={copyActivityReport}
            />
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <ActivityReportFilter />
        <ActivityReportTable />
      </CardBody>
    </Card>
  );
}